import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { PageTitle } from '../../../../../../../_metronic/layout/core'
import { Table }     from '../../../../../../../components/Table'
import { TableHead } from '../../../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../../../components/Table/TableBody'
import { getRequisiteOperations } from '../../core/api'
import { IRequisiteBalanceOperation } from '../../core/response.types'
import { REQUISITES_BREADCRUMBS } from '../../core/consts'

const fmtNum = (n: number) => new Intl.NumberFormat('ru-RU').format(n)
const fmtAmt = (n: number) => (n >= 0 ? '+' : '') + fmtNum(n)

const RequisiteHistoryPage = () => {
  const { id }       = useParams<{ id: string }>()
  const location     = useLocation()
  const requisiteId  = parseInt(id ?? '0', 10)

  const breadCrumbs = useMemo(
    () => REQUISITES_BREADCRUMBS.map(b => ({ ...b, isActive: location.pathname === b.path })),
    [location.pathname]
  )

  const [items, setItems]         = useState<IRequisiteBalanceOperation[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [loading, setLoading]     = useState(false)
  const [dateStart, setDateStart] = useState('')
  const [dateEnd,   setDateEnd]   = useState('')

  const load = useCallback(async () => {
    if (!requisiteId) return
    setLoading(true)
    try {
      const res = await getRequisiteOperations(requisiteId, {
        dateStart: dateStart || undefined,
        dateEnd:   dateEnd   || undefined,
      })
      setItems(res.data.data ?? [])
      setTotalRows(res.data.totalRows ?? 0)
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [requisiteId, dateStart, dateEnd])

  useEffect(() => { load() }, [load])

  return (
    <>
      <PageTitle breadcrumbs={breadCrumbs}>{`История реквизита #${requisiteId}`}</PageTitle>
      <div className='card'>
        <div className='card-header border-0 pt-6 d-flex flex-wrap gap-3 align-items-end'>
          <div>
            <label className='form-label'>Дата с</label>
            <input
              type='date'
              className='form-control form-control-sm'
              value={dateStart}
              onChange={e => setDateStart(e.target.value)}
            />
          </div>
          <div>
            <label className='form-label'>Дата по</label>
            <input
              type='date'
              className='form-control form-control-sm'
              value={dateEnd}
              onChange={e => setDateEnd(e.target.value)}
            />
          </div>
          <button className='btn btn-sm btn-primary' onClick={load}>Применить</button>
          {(dateStart || dateEnd) && (
            <button className='btn btn-sm btn-light' onClick={() => { setDateStart(''); setDateEnd('') }}>
              Сбросить
            </button>
          )}
          <span className='text-muted ms-auto'>Всего: {totalRows}</span>
        </div>
        <div className='card-body py-3'>
          <Table>
            <TableHead>
              <tr>
                <th>Дата</th>
                <th>Тип</th>
                <th>Сумма</th>
                <th>Баланс до</th>
                <th>Баланс после</th>
                <th>Комментарий</th>
                <th>Кто</th>
              </tr>
            </TableHead>
            <TableBody>
              {loading ? (
                <tr><td colSpan={7} className='text-center'>Загрузка…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className='text-center'>Нет операций</td></tr>
              ) : items.map(op => (
                <tr key={op.id}>
                  <td>{op.createdAt}</td>
                  <td>{op.operationType}</td>
                  <td className={op.amount >= 0 ? 'text-success' : 'text-danger'}>{fmtAmt(op.amount)}</td>
                  <td>{fmtNum(op.balanceBefore)}</td>
                  <td>{fmtNum(op.balanceAfter)}</td>
                  <td className='text-break'>{op.comment}</td>
                  <td>{op.createdBy ?? '—'}</td>
                </tr>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  )
}

export default RequisiteHistoryPage
